import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useDocumentFootnotes } from 'context/DocumentFootnotesContext';
import CitationService from 'services/citations';
import { AIFindingValueViewerProps } from './AIFindingValueViewer.props';
import { getAIFindingBoxSx } from './AIFindingValueViewer.styles';
import AISecurityFindingPopover from './AISecurityFindingPopover';
import FastValueViewer from '../../../../../components/ScalarSpreadsheet/utilities/FastValueViewer';
import { useAIFieldAcceptContext } from '../captable-table/AIFieldAcceptContext';

const AIFindingValueViewer = ({
  value,
  aiFieldMetadata,
  aiAccuracyConfidence,
  fieldLabel,
  securityId,
  fieldAlias,
  onRemoveFinding,
  originalValueViewer: OriginalValueViewer,
  ...restProps
}: AIFindingValueViewerProps) => {
  const [ctrlHeld, setCtrlHeld] = useState(false);
  const [, setLocalVersion] = useState(0);
  const { acceptField, dismissField } = useAIFieldAcceptContext();
  const { getDocumentMetadata } = useDocumentFootnotes();
  const citationService = useMemo(() => new CitationService(), []);

  const isAlreadyActioned = !!aiFieldMetadata.is_accepted;

  const handleAccept = useCallback(() => {
    if (securityId && fieldAlias) {
      acceptField(securityId, fieldAlias);
    }
  }, [securityId, fieldAlias, acceptField]);

  const handleDismiss = useCallback(() => {
    if (securityId && fieldAlias) {
      dismissField(securityId, fieldAlias);
    }
  }, [securityId, fieldAlias, dismissField]);

  const pendingCitation = useMemo(
    () => restProps.cell?.citationReferences?.find((c: any) => c.is_accepted === false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [restProps.cell?.citationReferences]
  );

  const pendingCitationDocumentName = useMemo(() => {
    if (!pendingCitation) {
      return undefined;
    }
    return getDocumentMetadata(pendingCitation.document_reference)?.documentName;
  }, [pendingCitation, getDocumentMetadata]);

  const handleAcceptCitation = useCallback(() => {
    if (!pendingCitation?.id || !restProps.cell?.citationReferences) {
      return;
    }

    const citationId = pendingCitation.id;

    // Update local cell state immediately for instant UI feedback
    // eslint-disable-next-line no-param-reassign
    restProps.cell.citationReferences = restProps.cell.citationReferences.map((c: any) =>
      c.id === citationId ? { ...c, is_accepted: true } : c
    );
    setLocalVersion(v => v + 1);

    citationService.updateCitationAcceptance(citationId, true).catch(() => {
      // Revert on failure
      // eslint-disable-next-line no-param-reassign
      restProps.cell.citationReferences = restProps.cell.citationReferences?.map((c: any) =>
        c.id === citationId ? { ...c, is_accepted: false } : c
      );
      setLocalVersion(v => v + 1);
    });
  }, [pendingCitation, restProps.cell, citationService]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        setCtrlHeld(true);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) {
        setCtrlHeld(false);
      }
    };
    const onBlur = () => setCtrlHeld(false);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  return (
    <AISecurityFindingPopover
      fieldMetadata={aiFieldMetadata}
      accuracyConfidence={aiAccuracyConfidence}
      fieldLabel={fieldLabel}
      onAccept={!isAlreadyActioned && securityId && fieldAlias ? handleAccept : undefined}
      // eslint-disable-next-line no-nested-ternary
      onRemove={!isAlreadyActioned ? (securityId && fieldAlias ? handleDismiss : onRemoveFinding) : undefined}
      pendingCitation={pendingCitation}
      onAcceptCitation={pendingCitation ? handleAcceptCitation : undefined}
      pendingCitationDocumentName={pendingCitationDocumentName}>
      {({ onClick }) => (
        <Box
          /*
          If Ctrl/Meta was held during the click, prevent default + stop propagation
          to the children and thus let the onClickCapture below open the AI popover.
          */
          onMouseDownCapture={e => {
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation?.();
            }
          }}
          onClickCapture={e => {
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation?.();
              onClick(e);
            }
          }}
          onContextMenuCapture={e => {
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation?.();
              onClick(e);
            }
          }}
          sx={getAIFindingBoxSx(ctrlHeld)}>
          {OriginalValueViewer ? (
            <OriginalValueViewer value={value} {...restProps} />
          ) : (
            <FastValueViewer value={value} {...restProps} />
          )}
        </Box>
      )}
    </AISecurityFindingPopover>
  );
};

export default AIFindingValueViewer;
